import { useEffect, useRef } from 'react'
import type { ExecutionResult } from '../execution-context'

interface SubmissionSuccessOptions {
  executionResult: ExecutionResult | null
  executionMode: 'run' | 'submit' | 'raw' | null
  isExecuting: boolean
  onSuccess: (executionId: string | null) => void
}

/**
 * Hook that detects when a code submission completes successfully with all tests passing.
 * Calls onSuccess callback with the execution ID when this happens.
 */
export function useSubmissionSuccess({
  executionResult,
  executionMode,
  isExecuting,
  onSuccess,
}: SubmissionSuccessOptions) {
  const prevIsExecuting = useRef(false)
  const prevResult = useRef<ExecutionResult | null>(null)

  useEffect(() => {
    const wasExecuting = prevIsExecuting.current
    const isNotExecutingAnymore = !isExecuting
    const isSubmitMode = executionMode === 'submit'
    const hasResult = executionResult !== null
    const resultChanged = executionResult !== prevResult.current

    // Detect transition from executing to complete in submit mode with new result
    if (
      wasExecuting &&
      isNotExecutingAnymore &&
      isSubmitMode &&
      hasResult &&
      resultChanged
    ) {
      // Check if all tests passed (streaming result format)
      const allTestsPassed =
        'success' in executionResult &&
        executionResult.success &&
        'summary' in executionResult &&
        executionResult.summary.total > 0

      if (allTestsPassed) {
        const executionId =
          'executionId' in executionResult ? executionResult.executionId : null
        onSuccess(executionId)
      }
    }

    prevIsExecuting.current = isExecuting
    prevResult.current = executionResult
  }, [executionResult, executionMode, isExecuting, onSuccess])
}
